import type { ComponentDocs } from '../component-docs.js';

/** A round picture with initials, standing in for a person's photo. */
const face = (initials: string, fill: string): string =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="${fill}"/>` +
      `<text x="16" y="21" font-family="sans-serif" font-size="13" font-weight="600" fill="white" text-anchor="middle">${initials}</text></svg>`,
  )}`;

/**
 * What `<caos-pill-container>` says about itself.
 *
 * IT MANAGES TAGS; IT IS NOT ONE. The pills are `<caos-tag>` elements placed inside it, and everything
 * a tag does — its avatar, its error state, its dismiss control and the event that reports it — stays
 * the tag's. What the container adds is what a row of them needs and a single tag cannot know about:
 * one tab stop for the whole row, arrow keys between the pills, Delete to ask for one to go, and a
 * limit past which the rest wait behind "+N more".
 *
 * REMOVAL IS STILL THE HOST'S. Delete on a removable pill raises the tag's own `caos-tag-remove`, from
 * the tag, and nothing more: the pill stays until the page takes it off, exactly as it does when its
 * dismiss control is pressed. The container only notices that it has gone and keeps focus in the row.
 * The removable example below declares the page's half so the key does something.
 */
const docs: ComponentDocs = {
  aliases: ['pill list', 'chip list', 'chip group', 'tag list', 'token list', 'filter chips', 'selected values'],

  answers: [
    'show the values of a multi-select field',
    'show which filters are applied, and let somebody take one off',
    'keep a long row of labels to a few with "+N more"',
    'move between tags with the arrow keys',
    'show the people on a record as pills with their pictures',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'limit',
      attribute: 'limit',
      property: true,
      type: 'number',
      description:
        'How many pills show before the rest go behind a "+N more" control, which shows them and becomes ' +
        '"Show fewer". Unset, every pill shows and the row wraps.',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description: 'What the pills are, read as the name of the list — "Applied filters", "Team", "Tags".',
    },
  ],

  events: [
    {
      name: 'caos-tag-remove',
      when:
        'Delete or Backspace is pressed on a removable pill. It is raised BY THE PILL, exactly as its own dismiss ' +
        'control raises it, so one listener at the container hears both; the pill stays until the host removes it.',
      carries: 'nothing — the pill is the event target.',
    },
  ],

  state: [{ name: 'expanded', type: 'boolean', description: 'Whether the pills past the limit are showing.' }],

  slots: [
    {
      name: '',
      takes:
        'The pills: caos-tag elements, drawn in the order they are placed. Each is made a list item and given its ' +
        'place in the row\'s single tab stop.',
    },
  ],

  tokens: [
    { token: '--caos-space-1', controls: 'The gap between pills, across and down.' },
    { token: '--caos-space-2', controls: 'The side padding of the "+N more" control.' },
    { token: '--caos-color-accent', controls: 'The lettering of the "+N more" control.' },
    { token: '--caos-font', controls: 'The typeface of the "+N more" control.' },
    { token: '--caos-radius-sm', controls: 'The corners of the "+N more" control\'s focus ring.' },
    { token: '--caos-focus-ring', controls: 'The ring on the "+N more" control reached by keyboard.' },
  ],

  parts: [
    { part: 'container', is: 'the row that holds the pills' },
    { part: 'more', is: 'the "+N more" / "Show fewer" control' },
  ],

  methods: [],

  placing:
    'Wherever a row of tags would go — under a record header, as a field\'s read value, above a filtered list. It ' +
    'is a block that wraps its pills with a small gap and takes the width it is given; give it a limit where that ' +
    'width cannot hold every pill it may be handed.',

  whenToUse:
    'A set of values that belong together is shown as tags — the values of a multi-select, the filters applied to a ' +
    'list, the people on a record — and there may be enough of them that the row needs a limit, or that a keyboard ' +
    'user should not have to tab through every one.',

  insteadUse: [
    {
      name: 'Tag',
      when: 'There is one label, or two sitting in a line of text, where a single tab stop and an overflow have nothing to manage.',
    },
    {
      name: 'PicklistField',
      when: 'The values are being CHOSEN rather than shown: a multi-select edits with the picklist field\'s several-values presentation, and reads with this.',
    },
    { name: 'SegmentedFilter', when: 'The chips choose what a list shows from a fixed set, rather than showing values that are there.' },
  ],

  accessibility:
    'The row is a `role="list"` named by label and each pill a `role="listitem"`, so a screen reader says how many ' +
    'there are before reading them. The row is one tab stop: arrow keys, Home and End move between pills, Left and ' +
    'Right following the reading direction, and Delete or Backspace on a removable pill asks for it to be removed, ' +
    'with focus staying in the row once it has gone. A tag placed here takes its own dismiss control out of the tab ' +
    'order, since Delete does that job, and each removable pill names Delete and Backspace in `aria-keyshortcuts`; ' +
    'the dismiss control still works by pointer. Pills behind "+N more" are out of the tab order until shown, ' +
    'and the control carries `aria-expanded` and says how many it holds. Left to the author: a label for the row, ' +
    'and a host listening for `caos-tag-remove` wherever pills are removable — Delete on a pill nobody removes ' +
    'leaves it where it is.',

  examples: [
    {
      key: 'people',
      label: 'Pills that represent people',
      summary: 'The people on a record, each tag carrying their picture in its avatar slot, read as one named list.',
      placement: {
        component: 'caos-pill-container',
        inputs: { label: 'Team' },
        children: [
          { component: 'caos-tag', children: [{ component: 'img', slot: 'avatar', inputs: { src: face('DR', '#4f5bd5'), alt: '' } }, 'Dana Reyes'] },
          { component: 'caos-tag', children: [{ component: 'img', slot: 'avatar', inputs: { src: face('MO', '#1f8a70'), alt: '' } }, 'Malik Osei'] },
          { component: 'caos-tag', children: [{ component: 'img', slot: 'avatar', inputs: { src: face('PL', '#b54708'), alt: '' } }, 'Priya Lal'] },
        ],
      },
    },
    {
      key: 'overflow',
      label: 'Past a limit',
      summary: 'Nine labels held to four, the other five behind "+5 more", with a way back to fewer once shown.',
      placement: {
        component: 'caos-pill-container',
        inputs: { label: 'Tags', limit: 4 },
        children: ['Rush', 'Repeat customer', 'Site visit needed', 'Freight', 'Net 30', 'Tax exempt', 'Key account', 'Referral', 'Spanish'].map(
          (word) => ({ component: 'caos-tag', children: [word] }),
        ),
      },
    },
    {
      key: 'removable',
      label: 'Removing from the keyboard',
      summary:
        'Applied filters a person takes off with the dismiss control or with Delete, focus staying in the row as each goes.',
      placement: {
        component: 'caos-pill-container',
        inputs: { label: 'Applied filters' },
        children: ['Stage: Proposal', 'Owner: Me', 'Region: North', 'Amount over 10,000'].map((word) => ({
          component: 'caos-tag',
          inputs: { removable: true },
          children: [word],
        })),
      },
      host: {
        summary: 'The surrounding page takes a filter off when its pill reports the request — the pill and the container only report it.',
        run: (stage: HTMLElement): void => {
          for (const container of Array.from(stage.querySelectorAll('caos-pill-container'))) {
            container.addEventListener('caos-tag-remove', (event) => (event.target as Element).remove());
          }
        },
      },
    },
    {
      key: 'with-an-error',
      label: 'A value in error',
      summary: 'Recipients where one address bounced: that tag turns red and says so in words as well as in colour.',
      placement: {
        component: 'caos-pill-container',
        inputs: { label: 'Recipients' },
        children: [
          { component: 'caos-tag', children: ['dana@meridian.example'] },
          { component: 'caos-tag', inputs: { error: true }, children: ['orders@meridan.example'] },
          { component: 'caos-tag', children: ['malik@meridian.example'] },
        ],
      },
    },
  ],
};

export default docs;
